import React, { useEffect, useState } from "react";
import { Box, Drawer } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "../components/navbar/navbar";
import Sidebar from "../components/sidebar/sidebar";

/**
 * Org dashboard shell: permanent sidebar on lg+, a temporary Drawer (same
 * Sidebar) on small screens toggled from the Navbar hamburger, and the routed
 * page in the content area. Role-aware nav lives inside Sidebar/Navbar.
 */
const DashLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  // Belt-and-suspenders close on every route change (covers browser
  // back/forward and any navigation that doesn't go through Sidebar's
  // onNavigate). The real fix for the "stays open" bug is the aria-hidden
  // CSS rule on the Drawer below — see that comment for why.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <Box sx={{ display: "flex", height: "100dvh", overflow: "hidden", bgcolor: "background.default" }}>
      {/* Permanent sidebar (lg and up) */}
      <Box sx={{ display: { xs: "none", lg: "block" }, flexShrink: 0 }}>
        <Sidebar />
      </Box>

      {/* Mobile drawer.
          Closing via a Sidebar Link click (component={Link}, not the
          backdrop) reliably flips MUI to its closed state — the root gets
          aria-hidden="true" — but a React Router navigation firing in the
          same reconciliation reliably stalls the Slide exit transition
          before it ever applies its off-screen transform: the drawer stays
          visually planted at transform:none, full-opacity backdrop and all,
          on top of the page it just navigated to. Confirmed this is
          specific to Link-triggered route changes (closing on a Link to the
          *current* page, no navigation, animates correctly; closing via the
          backdrop, no navigation, is also always correct); not size- or
          route-specific. Rather than depend on that transition's internal
          math, force it hidden directly off the state MUI does set
          correctly. */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { lg: "none" },
          '&[aria-hidden="true"]': { visibility: "hidden", pointerEvents: "none" },
        }}
        PaperProps={{ sx: { border: "none" } }}
      >
        <Sidebar onNavigate={() => setMobileOpen(false)} />
      </Drawer>

      {/* Main column — full height; only the content region scrolls */}
      <Box sx={{ flexGrow: 1, minWidth: 0, height: "100dvh", display: "flex", flexDirection: "column" }}>
        <Navbar onMenuClick={() => setMobileOpen(true)} />
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            // Page gutter. Was 16/24/32px, which pushed every table a long way
            // off the sidebar and cost real width on the data-dense pages;
            // 12/16/20 keeps the content clear of the chrome without wasting
            // a third of the gutter on empty ground.
            p: { xs: 1.5, sm: 2, lg: 2.5 },
            bgcolor: "background.default",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default DashLayout;
